import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PanelModule } from 'primeng/panel';
import { MessageModule } from 'primeng/message';
import { Button } from 'primeng/button';
import { RouterModule } from '@angular/router';
import { Message } from 'src/api/messages';
import moment from 'moment';
import { PingLinkComponent } from '../ping-link/ping-link.component';
import { extractIpv4 } from '../ip-utils';

@Component({
    selector: 'app-messages-panel',
    standalone: true,
    imports: [CommonModule, PanelModule, MessageModule, Button, RouterModule, PingLinkComponent],
    templateUrl: './messages-panel.component.html',
    styleUrl: './messages-panel.component.scss',
    host: { '[class.subdued]': 'subdued' },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagesPanelComponent {
    /** Full list of messages to display */
    @Input() messages: Message[] = [];
    /** Max number of messages to show before offering link */
    @Input() count = 0;
    /** Neutral rows with severity accents for a dense dashboard summary. */
    @Input() subdued = false;

    limitedMessages(): Message[] {
        const sorted = this.sortMessages(this.messages || []);
        if (this.count === 0) {
            return sorted;
        }
        return sorted.slice(0, this.count);
    }

    isTruncated(): boolean {
        return this.count > 0 && (this.messages?.length || 0) > this.count;
    }

    /**
     * Diagnostic messages carry no dedicated IP field, so recover one from the text to offer
     * a ping shortcut. Returns null when the message mentions no address.
     */
    pingHost(msg: Message): string | null {
        return extractIpv4(msg.message) ?? extractIpv4(msg.details);
    }

    formatDetails(msg: Message): string | undefined {
        if (msg.detailsTimestampUtc) {
            const m = moment.utc(msg.detailsTimestampUtc);
            if (m.isValid()) {
                const duration = moment.duration(moment.utc().diff(m));
                const label = msg.details ?? 'Duration';
                return `${label} ${duration.humanize()}`;
            }
        }
        return msg.details;
    }

    private sortMessages(messages: Message[]): Message[] {
        const levelOrder: Record<string, number> = {
            'error': 4,
            'warn': 3,
            'success': 2,
            'info': 1
        };

        return [...messages].sort((a, b) => {
            // First by level (highest severity first)
            const levelDiff = (levelOrder[b.level] || 0) - (levelOrder[a.level] || 0);
            if (levelDiff !== 0) return levelDiff;

            // Then by publisher name
            const pubA = a.publisherName || '';
            const pubB = b.publisherName || '';
            const pubCompare = pubA.localeCompare(pubB);
            if (pubCompare !== 0) return pubCompare;

            // Finally by time (newest first)
            const timeA = a.time || '';
            const timeB = b.time || '';
            return timeB.localeCompare(timeA);
        });
    }
}
